import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Wordmark from '../components/Wordmark';
import ContactForm from '../components/ContactForm';

/**
 * ContactPage — public-facing /contact route. Mirrors the MarketingPage
 * chrome (header + wordmark + CTA, footer with cross-page links) but
 * swaps the markdown body for the ContactForm component.
 *
 * Walter 2026-05-10: kept the chrome inline rather than abstracting a
 * shared layout component out of MarketingPage — the chrome is small,
 * MarketingPage's body is tightly coupled to its markdown rendering,
 * and a shared "MarketingChrome" wrapper would mean refactoring two
 * working surfaces just to add a third. Three similar copies > a
 * premature shared layout.
 */
export default function ContactPage({ user }) {
  const navigate = useNavigate();
  const isAuthed = !!user && !user.isAnonymous;
  const ctaHref = isAuthed ? '/board' : '/';
  const ctaLabel = isAuthed ? 'Open your boards →' : 'Start using listicity →';

  useEffect(() => {
    document.title = 'contact · listicity';
  }, []);

  return (
    <div style={{
      minHeight: '100dvh',
      background: 'var(--bg)',
      color: 'var(--text)',
      fontFamily: 'var(--sans)',
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Header — wordmark links home; CTA does the right thing
          depending on auth state. Identical to MarketingPage's. */}
      <header style={{
        height: 'var(--header-h)',
        background: 'var(--surface)',
        borderBottom: '1px solid var(--border)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 20px',
        flexShrink: 0,
      }}>
        <Link to="/landing" style={{ textDecoration: 'none' }}>
          <Wordmark fontSize={16} />
        </Link>
        <button
          onClick={() => navigate(ctaHref)}
          style={{
            background: 'var(--accent)',
            color: 'var(--bg)',
            border: 'none',
            padding: '7px 14px',
            borderRadius: 'var(--radius)',
            fontFamily: 'var(--mono)',
            fontSize: '12px',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          {ctaLabel}
        </button>
      </header>

      <main style={{
        flex: 1,
        display: 'flex',
        justifyContent: 'center',
        padding: '40px 20px 60px',
      }}>
        <div style={{ width: '100%', maxWidth: 720 }}>
          <header style={{ textAlign: 'center', marginBottom: '32px' }}>
            <h1 style={{
              fontFamily: 'var(--mono)',
              fontSize: '28px',
              fontWeight: 700,
              color: 'var(--text)',
              margin: '0 0 12px',
              letterSpacing: '-0.5px',
            }}>
              contact
            </h1>
            <p style={{
              fontFamily: 'var(--sans)',
              fontSize: '15px',
              color: 'var(--text-dim)',
              lineHeight: 1.55,
              margin: 0,
              maxWidth: 480,
              marginLeft: 'auto',
              marginRight: 'auto',
            }}>
              Question, bug, feature idea, or just saying hi — drop us a line. Replies usually come from Tom within a day or two.
            </p>
          </header>
          <ContactForm user={user} />
        </div>
      </main>

      {/* Footer — same cross-page links as MarketingPage, with /contact
          marked active. */}
      <footer style={{
        borderTop: '1px solid var(--border)',
        padding: '24px 20px',
        background: 'var(--surface)',
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        gap: '20px',
        fontFamily: 'var(--mono)',
        fontSize: '12px',
        color: 'var(--text-dim)',
      }}>
        <Link to="/landing" style={footerLinkStyle(false)}>home</Link>
        <Link to="/about"   style={footerLinkStyle(false)}>about</Link>
        <Link to="/help"    style={footerLinkStyle(false)}>help</Link>
        <Link to="/privacy" style={footerLinkStyle(false)}>privacy</Link>
        <Link to="/terms"   style={footerLinkStyle(false)}>terms</Link>
        <Link to="/contact" style={footerLinkStyle(true)}>
          <span style={{ marginRight: 4 }}>✉</span>contact
        </Link>
      </footer>
    </div>
  );
}

function footerLinkStyle(active) {
  return {
    color: active ? 'var(--accent)' : 'var(--text-dim)',
    textDecoration: 'none',
    fontWeight: active ? 600 : 400,
  };
}
